import { graphql } from 'gatsby'
import ImagesLoaded from 'imagesloaded'
import React, { useCallback, useContext, useEffect, useRef } from 'react'
import initTealiumTracker from 'tealium-tracker'
import { StoreDispatch } from '../Store'
import { teliumDefaults } from '../tracking/telium'

// Components
import AccordionSection from '../components/AccordionSection'
import Amenities from '../components/Amenities'
import Carousel from '../components/Carousel'
import Footer from '../components/Footer'
import FullWidthBanner from '../components/FullWidthBanner'
import ImageContentSplit from '../components/ImageContentSplit'
import Map from '../components/Map'
import Newsletter from '../components/Newsletter'
import Seo from '../components/Seo'
import Spacer from '../components/Spacer'
import Summary from '../components/Summary'
import Text from '../components/Text'
import VideoHero from '../components/VideoHero'
import WayToBook from '../components/WayToBook'
import { animation } from '../styles/vars/animation.style'
import { scrollToId } from '../utils/utils'

const IndexPage = ({ data, location }) => {
  const {
    contentful_id,
    heroTagline,
    heroHeading,
    heroBackgroundImage,
    pageContent,
    seoTitle,
    seoDescription,
    seoImage,
    heroVideoUrl,
  } = data.contentfulPageHome

  const {
    viewFullItinerary,
    viewDetailedItinerary,
    exploreTheDetails,
    previousSlide,
    nextSlide,
  } = data.contentfulGlobalSettings

  const dispatch = useContext(StoreDispatch)

  const wrapperRef = useRef()

  useEffect(() => {
    const { trackPageLoad } = initTealiumTracker()

    trackPageLoad({
      pageName: 'ultimatecruise:home',
      ...teliumDefaults,
    })
  }, [])

  const loaded = useCallback(
    img => {
      setTimeout(() => {
        dispatch({ type: 'END_INITIAL_WAIT' })
        dispatch({ type: 'HIDE_TRANSITION_MASK' })
      }, 0)
    },
    [dispatch]
  )

  useEffect(() => {
    const query = location.search

    const timeout = setTimeout(() => {
      if (query.length) {
        const id = query.substring(1).split('&')[0]
        scrollToId(id, dispatch, location)
      }
    }, (animation.maskShowDuration + animation.maskHideDuration) * 1000 + 2000)

    if (!wrapperRef.current) return
    dispatch({ type: 'BEGIN_INITIAL_WAIT' })
    const imageLoadTrack = new ImagesLoaded(wrapperRef.current)

    imageLoadTrack.on('always', loaded)

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
      imageLoadTrack.off('always', loaded)
    }
  }, [location, dispatch, wrapperRef, loaded])

  return (
    <>
      <Seo
        title={seoTitle}
        description={seoDescription}
        image={seoImage.file.url}
        pathname={`/`}
      />

      <VideoHero
        id={`${contentful_id}-hero`}
        tagline={heroTagline}
        heading={heroHeading}
        image={heroBackgroundImage}
        videoUrl={heroVideoUrl}
        explore={exploreTheDetails}
      />

      {pageContent.map((section, key) => {
        if (section.__typename === `ContentfulSectionFullWidthBanner`) {
          return (
            <FullWidthBanner
              id={section.contentful_id}
              label={section.date}
              labelColor={section.dateColour}
              title={section.title}
              text={section.copy}
              image={section.backgroundImage}
              button={section.button}
              key={key}
              hasHeadingOne={false}
              smallImage={section.smallImage}
              linkId={section.sectionId}
            />
          )
        }
        if (section.__typename === `ContentfulSectionMap`) {
          return (
            <Map
              id={section.contentful_id}
              heading={section.title}
              text={section.copy}
              key={key}
              link={viewFullItinerary}
              section1Dates={section.section1Dates}
              section1Title={section.section1Title}
              section1Copy={section.section1Copy}
              section2Dates={section.section2Dates}
              section2Title={section.section2Title}
              section2Copy={section.section2Copy}
              section3Dates={section.section3Dates}
              section3Title={section.section3Title}
              section3Copy={section.section3Copy}
              section4Dates={section.section4Dates}
              section4Title={section.section4Title}
              section4Copy={section.section4Copy}
              linkId={section.sectionId}
            />
          )
        }
        if (section.__typename === `ContentfulSectionIconList`) {
          return (
            <Amenities
              id={section.contentful_id}
              heading={section.heading}
              text={section.copy}
              disclaimer={section.smallText}
              listItems={section.listItems}
              key={key}
              linkId={section.sectionId}
            />
          )
        }
        if (section.__typename === `ContentfulSectionNewsletterForm`) {
          return (
            <Newsletter
              id={section.contentful_id}
              color={section.backgroundColor}
              heading={section.heading}
              text={section.copy}
              image={section.image}
              imageLabel={section.image.description}
              key={key}
              linkId={section.sectionId}
              data={section}
            />
          )
        }
        if (section.__typename === `ContentfulSectionText`) {
          return (
            <React.Fragment key={key}>
              <Spacer size={[100, 250]} />
              <Text
                heading={section.heading}
                text={section.copy}
                linkId={section.sectionId}
              />
            </React.Fragment>
          )
        }
        if (section.__typename === `ContentfulSectionImageContentSplit`) {
          return (
            <ImageContentSplit
              id={section.contentful_id}
              color={section.backgroundColor}
              images={[section.mainImage, section.secondaryImage]}
              text={section.text}
              link={viewDetailedItinerary}
              flip={section.mainImageAlignment === 'Left' ? false : true}
              secondaryImagePos={section.secondaryImagePosition}
              button1={section.buttonFirst}
              button2={section.button}
              key={key}
              linkId={section.sectionId}
            />
          )
        }
        if (section.__typename === `ContentfulSectionCarousel`) {
          return (
            <Carousel
              id={section.contentful_id}
              featured={section.featured === 'Yes' ? true : false}
              heading={section.heading}
              carouselItems={section.carouselItems}
              key={key}
              linkId={section.sectionId}
              nextSlideLabel={nextSlide}
              prevSlideLabel={previousSlide}
            />
          )
        }
        if (section.__typename === `ContentfulSectionAccordion`) {
          return (
            <AccordionSection
              id={section.contentful_id}
              heading={section.heading}
              content={section.accordionItems}
              key={key}
              button={section.button}
              linkId={section.sectionId}
              faq={section.faqSection}
              defaultOpen={0}
            />
          )
        }
        if (section.__typename === `ContentfulSectionSummary`) {
          return (
            <Summary
              id={section.contentful_id}
              heading={section.heading}
              text={section.text}
              additionalText={section.additionalText}
              button={section.button}
              image={section.backgroundImage}
              stat1={section.statistic1}
              stat2={section.statistic2}
              stat3={section.statistic3}
              stat1Image={section.statistic1Images}
              stat2Image={section.statistic2Images}
              stat3Image={section.statistic3Images}
              key={key}
              linkId={section.sectionId}
            />
          )
        }
        if (section.__typename === `ContentfulSectionCards`) {
          return (
            <WayToBook
              id={section.contentful_id}
              heading={section.blockHeading}
              intro={section.headingText}
              image={section.backgroundImage}
              cards={section.cards}
              key={key}
              forwardRef={wrapperRef}
            ></WayToBook>
          )
        }

        return <></>
      })}
      <Footer />
    </>
  )
}

export default IndexPage

export const pageQuery = graphql`
  query HomePageQuery {
    contentfulPageHome {
      contentful_id
      seoTitle
      seoDescription
      seoImage {
        file {
          url
        }
      }
      heroTagline
      heroHeading {
        raw
      }
      heroVideoUrl
      heroBackgroundImage {
        gatsbyImageData(width: 3840, height: 2160)
      }
      pageContent {
        __typename
        ... on ContentfulSectionFullWidthBanner {
          contentful_id
          date
          dateColour
          sectionId
          backgroundImage {
            title
            gatsbyImageData(width: 3840, height: 2040, quality: 35)
          }
          smallImage {
            gatsbyImageData(height: 300)
          }
          title {
            raw
          }
          copy {
            raw
          }
          button {
            ... on ContentfulItemInternalLink {
              internalLink: link {
                ... on ContentfulPageExpedition {
                  slug
                }
                ... on ContentfulPageItinerary {
                  slug
                }
                ... on ContentfulPageExpedition {
                  slug
                }
              }
              label
              anchorIdOptional
            }
            ... on ContentfulItemExternalLink {
              externalLink: link
              label
            }
          }
        }
        ... on ContentfulSectionCards {
          contentful_id
          blockHeading
          headingText {
            raw
          }
          backgroundImage {
            title
            gatsbyImageData(width: 1920, height: 1111, quality: 70)
          }
          cards {
            ... on ContentfulItemWayToBookCard {
              backgroundImage {
                title
                gatsbyImageData(width: 490, height: 390, quality: 70)
              }
              headingItalic
              headingNormal
              text
              link {
                ... on ContentfulItemExternalLink {
                  externalLink: link
                  label
                }
              }
            }
          }
        }
        ... on ContentfulSectionMap {
          contentful_id
          title {
            raw
          }
          sectionId
          copy {
            raw
          }
          section1Dates
          section1Title {
            raw
          }
          section1Copy
          section2Dates
          section2Title {
            raw
          }
          section2Copy
          section3Dates
          section3Title {
            raw
          }
          section3Copy
          section4Dates
          section4Title {
            raw
          }
          section4Copy
        }
        ... on ContentfulSectionAccordion {
          contentful_id
          heading {
            raw
          }
          sectionId
          faqSection
          accordionItems {
            title
            content {
              raw
              references {
                __typename
                ... on ContentfulItemTable {
                  contentful_id
                  __typename
                  table {
                    tableData
                  }
                }
              }
            }
          }
          button {
            ... on ContentfulItemInternalLink {
              internalLink: link {
                ... on ContentfulPageExpedition {
                  slug
                }
                ... on ContentfulPageItinerary {
                  slug
                }
                ... on ContentfulPageExpedition {
                  slug
                }
              }
              label
            }
            ... on ContentfulItemExternalLink {
              externalLink: link
              label
            }
          }
        }
        ... on ContentfulSectionIconList {
          contentful_id
          id
          sectionId
          heading {
            raw
          }
          copy {
            raw
          }
          smallText
          listItems {
            icon {
              gatsbyImageData(width: 200, height: 200)
            }
            iconSvg {
              file {
                url
                details {
                  image {
                    width
                  }
                }
              }
              description
            }
            title
            descriptionOptional
          }
        }
        ... on ContentfulSectionText {
          contentful_id
          id
          sectionId
          heading {
            raw
          }
          copy {
            raw
          }
        }
        ... on ContentfulSectionNewsletterForm {
          contentful_id
          sectionId
          backgroundColor
          heading {
            raw
          }
          copy {
            raw
          }
          image {
            gatsbyImageData(width: 1184, height: 1464)
            description
          }
          formFirstNameLabel
          formLastNameLabel
          formEmailAddressLabel
          formCountrylocationLabel
          formSignUpLabel
        }
        ... on ContentfulSectionCarousel {
          contentful_id
          heading {
            raw
          }
          sectionId
          featured
          carouselItems {
            title
            label
            image {
              gatsbyImageData
              description
            }
            featuredCaption {
              raw
            }
            link {
              ... on ContentfulItemInternalLink {
                internalLink: link {
                  ... on ContentfulPageExpedition {
                    slug
                  }
                  ... on ContentfulPageItinerary {
                    slug
                  }
                  ... on ContentfulPageExpedition {
                    slug
                  }
                }
                label
                anchorIdOptional
              }
              ... on ContentfulItemExternalLink {
                externalLink: link
                label
              }
            }
          }
        }
        ... on ContentfulSectionSummary {
          contentful_id
          heading {
            raw
          }
          sectionId
          text {
            raw
          }
          button {
            ... on ContentfulItemInternalLink {
              internalLink: link {
                ... on ContentfulPageExpedition {
                  slug
                }
                ... on ContentfulPageItinerary {
                  slug
                }
                ... on ContentfulPageExpedition {
                  slug
                }
              }
              label
              anchorIdOptional
            }
            ... on ContentfulItemExternalLink {
              externalLink: link
              label
            }
          }
          extraText
          additionalText {
            raw
          }
          statistic1
          statistic1Images {
            gatsbyImageData(width: 308, height: 224)
            description
          }
          statistic2
          statistic2Images {
            gatsbyImageData(width: 308, height: 224)
            description
          }
          statistic3
          statistic3Images {
            description
            gatsbyImageData(width: 308, height: 224)
          }
          backgroundImage {
            gatsbyImageData(width: 3840, height: 2040)
          }
        }
        ... on ContentfulSectionImageContentSplit {
          contentful_id
          id
          backgroundColor
          text {
            raw
          }
          sectionId
          buttonFirst {
            ... on ContentfulItemInternalLink {
              id
              internalLink: link {
                ... on ContentfulPageExpedition {
                  slug
                }
                ... on ContentfulPageItinerary {
                  slug
                }
                ... on ContentfulPageExpedition {
                  slug
                }
              }
              label
              anchorIdOptional
            }
            ... on ContentfulItemExternalLink {
              id
              externalLink: link
              label
            }
          }
          button {
            ... on ContentfulItemInternalLink {
              id
              internalLink: link {
                ... on ContentfulPageExpedition {
                  slug
                }
                ... on ContentfulPageItinerary {
                  slug
                }
                ... on ContentfulPageExpedition {
                  slug
                }
              }
              label
              anchorIdOptional
            }
            ... on ContentfulItemExternalLink {
              id
              externalLink: link
              label
            }
          }
          mainImage {
            gatsbyImageData(width: 1800, height: 2064)
            description
            title
          }
          mainImageAlignment
          secondaryImage {
            gatsbyImageData
            description
          }
          secondaryImagePosition
        }
      }
    }
    contentfulGlobalSettings {
      viewFullItinerary
      viewDetailedItinerary
      exploreTheDetails
      previousSlide
      nextSlide
    }
  }
`
